import { defineStore } from "pinia";
import type { ThemeState, RouteReloadOption, TabItem } from "./types";
import { TAB_KEEP_ALIVE, KEEP_ALIVE_EXCLUDES } from "@/config/setting";

const defaultState: ThemeState = Object.freeze({
  // 是否折叠侧边栏
  collapse: false,
  // 路由切换动画名称
  transitionName: "slide-right",
  // 需要缓存的组件
  tabs: [],
  // 是否开启页签栏
  showTabs: true,
  // 刷新路由时的参数
  routeReload: null,
  // 主页的组件名称
  homeComponents: [],
});

export const useThemeStore = defineStore({
  id: "theme",
  state: () => {
    const state = { ...defaultState };
    return state;
  },
  getters: {
    keepAliveInclude(): Array<string> {
      if (!TAB_KEEP_ALIVE || !this.showTabs) {
        return [];
      }
      const components = new Set<string>();
      const { reloadPath, reloadHome } = this.routeReload || {};
      this.tabs.forEach((item) => {
        const isExclude = KEEP_ALIVE_EXCLUDES.includes(item.path as string);
        const isReload = reloadPath && reloadPath === item.path;
        if (!isExclude && !isReload && item.components) {
          item.components?.forEach((component) => {
            if (typeof component === "string" && component) {
              components.add(component);
            }
          });
        }
      });
      if (!reloadHome) {
        this.homeComponents?.forEach((item) => {
          if (typeof item === "string" && item) {
            components.add(item);
          }
        });
      }
      return Array.from(components);
    },
  },
  actions: {
    setTabs(value: TabItem[]) {
      this.tabs = value;
    },
    setCollapse(value: boolean) {
      this.collapse = value;
    },
    /**
     * 设置刷新路由信息
     * @param option 路由刷新参数
     */
    setRouteReload(option: RouteReloadOption | null) {
      this.routeReload = option;
    },
    /**
     * @description: 添加页签或者更新相同 key 的页签数据
     * @param {TabItem} data
     * @return {*}
     */
    tabAdd(data: TabItem | TabItem[]) {
      if (Array.isArray(data)) {
        data.forEach((item) => {
          this.tabAdd(item);
        });
        return;
      }
      const index = this.tabs.findIndex((item) => item.key === data.key);
      if (index === -1) {
        this.setTabs(this.tabs.concat([data]));
      } else if (data.fullPath !== this.tabs[index].fullPath) {
        this.setTabs(
          this.tabs
            .slice(0, index)
            .concat([data])
            .concat(this.tabs.slice(index + 1))
        );
      }
    },
    /**
     * 设置主页路由对应的组件名称
     * @param components 组件名称
     */
    setHomeComponents(components: string[]) {
      this.homeComponents = components;
    },

    /**
     * @description: 删除页签
     * @return {*}
     */
    async tabRemove({
      key,
      active,
    }: {
      key: string;
      active: string;
    }): Promise<TabRemoveResult> {
      const i = this.tabs.findIndex((t) => t.key === key || t.fullPath === key);
      if (i === -1) {
        return {};
      }
      const t = this.tabs[i];
      // if (!t.closable) {
      //   return Promise.reject();
      // }
      const path = this.tabs[i - 1]?.fullPath;
      this.setTabs(this.tabs.filter((_d, j) => j !== i));
      return t.path === active
        ? { path, home: !path }
        : t.fullPath === active
        ? { path, home: !path }
        : {};
    },

    /**
     * 修改页签
     * @param data 页签数据
     */
    tabSetItem(data: TabItem) {
      let i = -1;
      if (data.key) {
        i = this.tabs.findIndex((d) => d.key === data.key);
      } else if (data.fullPath) {
        i = this.tabs.findIndex((d) => d.fullPath === data.fullPath);
      } else if (data.path) {
        i = this.tabs.findIndex((d) => d.path === data.path);
      }
      if (i !== -1) {
        const item = { ...this.tabs[i] };
        if (data.title) {
          item.title = data.title;
        }
        if (typeof data.closable === "boolean") {
          item.closable = data.closable;
        }
        if (data.components) {
          item.components = data.components;
        }
        this.setTabs(
          this.tabs
            .slice(0, i)
            .concat([item])
            .concat(this.tabs.slice(i + 1))
        );
      }
    },
  },
});
/**
 * 关闭页签返回类型
 */
export interface TabRemoveResult {
  // 关闭后要跳转的地址
  path?: string;
  // 关闭后是否跳转到主页
  home?: boolean;
}
